import { BASE_URL } from 'helpers/global.const.helper';
import { normalizeAuthorName } from 'helpers/structured-data';
import sortOpinions from 'helpers/sortOpinions';
import ProducerInterface from 'interfaces/producer.interface';
import ProductDescriptionInterface from 'modules/product/interfaces/product-description.interface';
import OpinionsInterface from 'interfaces/opinions.interface';
import OfferInterface from 'interfaces/offer.interface';
import PhotosInterface from 'interfaces/photo.interface';
import DealerInterface from 'interfaces/dealer.interface';
import { trimLength } from 'helpers/string-tools';

interface StructuralDataBrandInterface {
    '@type': 'Brand';
    name: string;
}

interface StructuralDataPersonInterface {
    '@type': 'Person';
    name: string;
}

interface StructuralDataRatingInterface {
    '@type': 'Rating';
    ratingValue: number;
    bestRating: string;
    worstRating: string;
}

interface StructuralDataReviewInterface {
    '@type': 'Review';
    name: string;
    datePublished: string;
    reviewBody: string;
    author: StructuralDataPersonInterface;
    reviewRating: StructuralDataRatingInterface;
}

interface StructuralDataAggregateRatingInterface {
    '@type': 'AggregateRating';
    bestRating: string;
    ratingCount: number;
    ratingValue: number;
    reviewCount: number;
    worstRating: string;
}

interface StructuralDataOrganizationInterface {
    '@type': 'Organization';
    name: string;
}

interface StructuralDataOfferInterface {
    '@type': 'Offer';
    '@id': string;
    url: string;
    priceCurrency: string;
    price: number;
    itemCondition: string;
    availability: string;
    seller: StructuralDataOrganizationInterface;
}

interface StructuralDataAggregateOfferInterface {
    '@type': 'AggregateOffer';
    availability?: string;
    lowPrice: number;
    highPrice?: number;
    offerCount: number;
    priceCurrency: string;
    offers?: StructuralDataOfferInterface[];
}

interface StructuralDataListItemInterface {
    '@type': 'ListItem';
    position: number;
    name: string;
}

interface StructuralDataItemListInterface {
    '@type': 'ItemList';
    itemListElement: StructuralDataListItemInterface[];
}

export interface StructuralDataProductInterface {
    '@context': string;
    '@type': 'Product';
    '@id': string;
    name: string;
    url: string;
    sku: number;
    mpn?: string;
    isbn?: string[] | number[];
    gtin13?: string;
    gtin12?: string;
    gtin8?: string;
    description?: string;
    image?: string[] | string;
    brand?: StructuralDataBrandInterface;
    aggregateRating?: StructuralDataAggregateRatingInterface;
    offers?: StructuralDataAggregateOfferInterface;
    review?: StructuralDataReviewInterface[] | StructuralDataReviewInterface;
    positiveNotes?: StructuralDataItemListInterface;
    negativeNotes?: StructuralDataItemListInterface;
}

interface ProductInterface {
    id: number;
    name: string;
    partNo?: string;
    isbn?: string[] | number[];
    url: string;
    category: {
        id?: string;
        name?: string;
    };
    photos: {
        main: PhotosInterface | null;
        items?: PhotosInterface[];
    };
    producer: ProducerInterface;
    ean?: string[];
    description: {
        text: ProductDescriptionInterface['text'];
        content?: ProductDescriptionInterface['content'];
    };
    opinions: OpinionsInterface;
    offers: {
        count: number;
        items: {
            id?: OfferInterface['id'];
            price: OfferInterface['price'];
            dealer: {
                name: DealerInterface['name'];
            };
        }[];
    };
    minPrice: number;
    maxPrice?: number;
}

const MAX_PHOTOS = 10;
const MAX_DESCRIPTION_LENGTH = 200;
const CATEGORY_BICYCLES_ID = 'c2215';

export default function getProductStructuralData(product: ProductInterface): StructuralDataProductInterface {
    const data: StructuralDataProductInterface = {
        '@context': 'http://schema.org/',
        '@type': 'Product',
        '@id': `https://www.skapiec.pl/#/schema/Product/listing-${product.id}`,
        'name': product.name,
        'url': `${BASE_URL}${product.url}`,
        'sku': product.id
    };

    if (product.partNo) {
        data.mpn = product.partNo;
    }

    if (product.isbn?.length) {
        data.isbn = product.isbn;
    }

    product.ean?.forEach(item => {
        if (!data.gtin13 && item.length === 13) {
            data.gtin13 = item;
        }

        if (!data.gtin12 && item.length === 12) {
            data.gtin12 = item;
        }

        if (!data.gtin8 && item.length === 8) {
            data.gtin8 = item;
        }
    });

    if (product.description?.text) {
        data.description = trimLength(product.description.text, MAX_DESCRIPTION_LENGTH);
    }

    if (product.photos?.main) {
        data.image = product.photos.main.large;
    }

    if (product.photos?.items && product.photos?.items?.length > 1) {
        data.image = product.photos.items.slice(0, MAX_PHOTOS).map(img => img.large);
    }

    if (product.producer?.name) {
        data.brand = {
            '@type': 'Brand',
            'name': product.producer.name
        };
    }

    if (product.opinions?.count) {
        data.aggregateRating = {
            '@type': 'AggregateRating',
            'bestRating': '5.0',
            'ratingCount': product.opinions.count,
            'ratingValue': product.opinions.average,
            'reviewCount': product.opinions.count,
            'worstRating': '1.0'
        };
    }

    if (product.offers.count || product.minPrice || product.maxPrice) {
        data.offers = {
            '@type': 'AggregateOffer',
            'lowPrice': product.minPrice,
            'highPrice': product.maxPrice || product.minPrice,
            'offerCount': product.offers.count,
            'availability': !product.offers.count ? 'https://schema.org/OutOfStock' : void 0,
            'priceCurrency': 'PLN',
            'offers': product.category?.id === CATEGORY_BICYCLES_ID
                ? product.offers.items.map(offer => ({
                    '@type': 'Offer',
                    '@id': `https://www.skapiec.pl/#/schema/Offer/listing-${offer.id}`,
                    'url': `${BASE_URL}${product.url}`,
                    'priceCurrency': 'PLN',
                    'price': offer.price,
                    'availability': 'https://schema.org/InStock',
                    'itemCondition': 'http://schema.org/NewCondition',
                    'seller': {
                        '@type': 'Organization',
                        'name': offer.dealer.name
                    }
                }))
                : void 0
        };
    }

    if (product.opinions?.items?.length) {
        const sortedOpinionsItems: StructuralDataReviewInterface[] = sortOpinions(product.opinions.items).slice(0, 10).map(item => ({
            '@type': 'Review',
            'datePublished': new Date(item.timestamp * 1000).toISOString().slice(0, 10),
            'name': `${product.name} - opinie`,
            'reviewBody': item.content,
            'author': {
                '@type': 'Person',
                'name': normalizeAuthorName(item.author)
            },
            'reviewRating': {
                '@type': 'Rating',
                'ratingValue': item.rating,
                'bestRating': '5.0',
                'worstRating': '1.0'
            },
            'positiveNotes': !item.pros?.length
                ? void 0
                : {
                    '@type': 'ItemList',
                    'itemListElement': item.pros.map((item, index) => ({
                        '@type': 'ListItem',
                        'position': index + 1,
                        'name': item
                    }))
                },
            'negativeNotes': !item.cons?.length
                ? void 0
                : {
                    '@type': 'ItemList',
                    'itemListElement': item.cons.map((item, index) => ({
                        '@type': 'ListItem',
                        'position': index + 1,
                        'name': item
                    }))
                }
        }));

        data.review = sortedOpinionsItems.length === 1 ? sortedOpinionsItems[0] : sortedOpinionsItems;
    }

    return data;
}
